import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

interface SeoConfig {
  title: string;
  description?: string;
  url?: string;
  image?: string;
  type?: string;
}

const DEFAULTS = {
  title: 'GitCharger | EV Charging Station Map | A smarter way to charge!',
  description: 'Find EV charging stations near you. Plan trips, check connector availability, read ratings, and navigate to chargers, all in one app.',
  image: 'https://gitcharger.com/logo-text.png',
  url: 'https://gitcharger.com',
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private meta: Meta,
    private titleService: Title,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  update(config: SeoConfig) {
    const title = config.title || DEFAULTS.title;
    const description = config.description || DEFAULTS.description;
    const url = config.url || DEFAULTS.url;
    const image = config.image || DEFAULTS.image;
    const type = config.type || 'website';

    this.titleService.setTitle(title);

    this.meta.updateTag({ name: 'description', content: description });

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:type', content: type });
    this.meta.updateTag({ property: 'og:site_name', content: 'GitCharger' });

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    // Canonical
    this.setCanonical(url);
  }

  reset() {
    this.update({ title: DEFAULTS.title, description: DEFAULTS.description });
  }

  private setCanonical(url: string) {
    let link: HTMLLinkElement | null = this.doc.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
